import { setFilterBarTestId } from "@bcc/components/finance/filter-bar.utils";
import { setInvoiceDetailDrawerTestId } from "@bcc/components/finance/invoice-detail-drawer.utils";
import { setInvoiceTableRowTestId, setInvoiceTableTestId } from "@bcc/components/finance/invoice-table.utils";
import { setFinanceInvoicesViewTestId } from "@bcc/components/views/finance-invoices-view.utils";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { setSidebarNavTourId } from "../tour-ids";
import type { FeatureTour } from "../tour.types";
import { DATA_TABLE_ROW_SELECTOR, testIdPrefixSelector, testIdSelector, tourTargetSelector } from "../tour.utils";

/** Steps whose target only exists conditionally (detail drawer, export button) skip quickly */
const CONDITIONAL_STEP_TIMEOUT_MS = 4000;

const INVOICE_TABLE = testIdSelector(setInvoiceTableTestId("container"));
/** Any invoice row — the detail drawer and the export button only exist once invoices do */
const INVOICE_ROW = `${INVOICE_TABLE} ${DATA_TABLE_ROW_SELECTOR}`;
const INVOICE_DETAIL_DRAWER = testIdSelector(setInvoiceDetailDrawerTestId("content"));

/** The invoice detail drawer's Radix close button (the only direct-child button of the sheet content) */
const CLOSE_INVOICE_DRAWER = { clickTarget: `${INVOICE_DETAIL_DRAWER} > button` };

export const financeTour: FeatureTour = {
  id: "finance",
  titleKey: "tour.features.finance.title",
  order: 5,
  requiredModule: "finance",
  steps: [
    {
      id: "finance.invoices-nav",
      route: ROUTE_PATHS.FINANCE_INVOICES,
      target: tourTargetSelector(setSidebarNavTourId("finance")),
      titleKey: "tour.steps.finance.invoicesNav.title",
      bodyKey: "tour.steps.finance.invoicesNav.body",
      placement: "right",
      requiredPage: "finance.invoices.view",
    },
    {
      id: "finance.search-filters",
      route: ROUTE_PATHS.FINANCE_INVOICES,
      target: testIdSelector(setFilterBarTestId("container")),
      titleKey: "tour.steps.finance.searchFilters.title",
      bodyKey: "tour.steps.finance.searchFilters.body",
      placement: "bottom",
      requiredPage: "finance.invoices.view",
    },
    {
      id: "finance.invoices-table",
      route: ROUTE_PATHS.FINANCE_INVOICES,
      target: INVOICE_TABLE,
      titleKey: "tour.steps.finance.invoicesTable.title",
      bodyKey: "tour.steps.finance.invoicesTable.body",
      placement: "top",
      requiredPage: "finance.invoices.view",
      // Closes the detail drawer when the user steps Back out of it
      prepare: [CLOSE_INVOICE_DRAWER],
      emptyState: {
        detectTarget: INVOICE_ROW,
        bodyKey: "tour.steps.finance.invoicesTable.emptyBody",
      },
    },
    {
      id: "finance.invoice-details",
      route: ROUTE_PATHS.FINANCE_INVOICES,
      target: INVOICE_DETAIL_DRAWER,
      titleKey: "tour.steps.finance.invoiceDetails.title",
      bodyKey: "tour.steps.finance.invoiceDetails.body",
      placement: "left",
      requiredPage: "finance.invoices.view",
      // Opens the drawer by clicking the first invoice-id cell (bubbles to the row)
      prepare: [{ clickTarget: testIdPrefixSelector(setInvoiceTableRowTestId("")) }],
      skipUnless: INVOICE_ROW,
      targetTimeoutMs: CONDITIONAL_STEP_TIMEOUT_MS,
    },
    {
      id: "finance.export",
      route: ROUTE_PATHS.FINANCE_INVOICES,
      target: testIdSelector(setFinanceInvoicesViewTestId("export-csv-button")),
      titleKey: "tour.steps.finance.export.title",
      bodyKey: "tour.steps.finance.export.body",
      placement: "bottom",
      requiredPage: "finance.invoices.view",
      // Closes the detail drawer left open by the previous step
      prepare: [CLOSE_INVOICE_DRAWER],
      // The export button only renders when invoices exist
      skipUnless: INVOICE_ROW,
      targetTimeoutMs: CONDITIONAL_STEP_TIMEOUT_MS,
    },
  ],
};
